import React, { CSSProperties, useEffect, useState } from "react";
import { darkTheme } from "@yleisradio/yds-core";
import styled from "styled-components";

import { useEmbedParametersContext } from "../contexts/embedParametersContext";
import { useMediaContext } from "../contexts/MediaContext";
import { secondsToHHMMSSFormat } from "../helpers/timeHelpers";
import { usePlayerStore } from "../store/usePlayerStore";
import { ScrubberImages } from "../types/player-api";

const SeekTooltipDiv = styled.div`
  pointer-events: none;
  position: absolute;
  width: 0;
`;

const SeekTitle = styled.div`
  font-size: 14px;
  position: absolute;
  transform: translateX(-50%);
  word-break: initial;
  z-index: 2;
`;

const SeekThumbnail = styled.div<{ width: number; height: number }>`
  background: ${darkTheme.BACKGROUND} no-repeat 0 0;
  border-radius: 4px;
  border: 1px solid ${darkTheme.BORDER};
  height: ${props => props.height}px;
  position: absolute;
  width: ${props => props.width}px;
  z-index: 1;
`;

const SeekThumbnailImage = ({
  duration,
  hoverPosition,
  scrubberImages,
  seekSlider
}: {
  duration: number;
  hoverPosition?: number | false;
  scrubberImages: ScrubberImages;
  seekSlider: HTMLInputElement | null;
}) => {
  const { useStoreState } = usePlayerStore();
  const playerElement = useStoreState.playerElement();
  const device = useStoreState.device();
  const [thumbnailStyle, setThumbnailStyle] = useState<CSSProperties>({
    visibility: "hidden"
  });
  const [currentImgNumber, setCurrentImgNumber] = useState(1);
  const { height: imgHeight, width: imgWidth } = scrubberImages.tiles;

  const maxImagesInRow = scrubberImages.matrix.width / imgWidth;
  const numberOfThumbnails =
    Math.ceil(duration / scrubberImages.tiles.interval) + 1; // +1 for the last thumbnail

  useEffect(() => {
    if (!seekSlider || !playerElement) return;
    const hoverPercent = hoverPosition as number;
    const padding = device.isMobile ? 8 : 20;
    const playerUIRect = playerElement.getBoundingClientRect();
    const seekSliderRect = seekSlider.getBoundingClientRect();
    const visibility = hoverPosition !== false ? "visible" : "hidden";
    const maxLeft =
      -imgWidth / 2 +
      seekSliderRect.width * hoverPercent +
      (seekSliderRect.left - playerUIRect.left);
    const maxRight = playerUIRect.width - padding - imgWidth;

    setCurrentImgNumber(
      Math.floor((numberOfThumbnails * (hoverPercent * duration)) / duration)
    );
    setThumbnailStyle({
      backgroundImage: `url(${scrubberImages.url})`,
      backgroundPositionX:
        -imgWidth * Math.floor(currentImgNumber % maxImagesInRow) + "px",
      backgroundPositionY:
        -imgHeight * Math.floor(currentImgNumber / maxImagesInRow) + "px",
      bottom: device.isMobile ? "75px" : "60px",
      left:
        Math.min(Math.max(maxLeft, padding), maxRight) -
        (device.isMobile ? 15 : 70) +
        "px",
      visibility
    });
  }, [
    currentImgNumber,
    imgHeight,
    imgWidth,
    numberOfThumbnails,
    maxImagesInRow,
    duration,
    scrubberImages.url,
    hoverPosition,
    seekSlider,
    playerElement,
    device.isMobile
  ]);

  return (
    <SeekThumbnail
      height={imgHeight}
      width={imgWidth}
      data-testid="seek-thumbnail"
      style={thumbnailStyle}
    />
  );
};

export const SeekTooltip = ({
  duration,
  hoverPosition,
  seekSlider
}: {
  duration: number | undefined;
  hoverPosition: number | false;
  seekSlider: HTMLInputElement | null;
}) => {
  const { useStoreState } = usePlayerStore();
  const { seekThumbnail } = useEmbedParametersContext();
  const { scrubberImages } = useMediaContext();
  const device = useStoreState.device();
  const isLive = useStoreState.isLive();

  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>({
    visibility: "hidden"
  });
  const [seekTitle, setSeekTitle] = useState("");

  useEffect(() => {
    if (!seekSlider) return;
    const hoverPercent = hoverPosition as number;
    const padding = device.isMobile ? 8 : 20;
    const seekSliderRect = seekSlider.getBoundingClientRect();
    const visibility = hoverPosition !== false ? "visible" : "hidden";

    const tooltipLeft = seekSliderRect.width * hoverPercent;
    setTooltipStyle({
      left: `${device.isMobile ? Math.max(padding, tooltipLeft) : tooltipLeft}px`,
      top: device.isMobile ? "-26px" : "-18px",
      visibility
    });
  }, [device, hoverPosition, seekSlider, duration]);

  useEffect(() => {
    if (hoverPosition && duration !== undefined) {
      let updatedSeekTitle = secondsToHHMMSSFormat(
        isLive ? duration * (1 - hoverPosition) : duration * hoverPosition
      );

      if (isLive && updatedSeekTitle) {
        updatedSeekTitle = "-" + updatedSeekTitle;
      }

      setSeekTitle(updatedSeekTitle);
    }
  }, [duration, hoverPosition, isLive]);

  return (
    <>
      <SeekTooltipDiv style={tooltipStyle}>
        {seekTitle && <SeekTitle>{seekTitle}</SeekTitle>}
      </SeekTooltipDiv>
      {seekThumbnail && scrubberImages && (
        <SeekThumbnailImage
          scrubberImages={scrubberImages}
          seekSlider={seekSlider}
          duration={duration ?? 0}
          hoverPosition={hoverPosition}
        />
      )}
    </>
  );
};
